import React, { useContext, useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { Web3Context } from '../../context/Web3Context';
import { formatUSDT, formatDate } from '../../utils/formatters';
import { PACKAGE_NAMES, PACKAGE_COLORS, ROYALTY_NAMES, INCOME_COLORS } from '../../utils/constants';
import { checkUserAllTiers, saveEligibility } from '../../utils/rewardEligibility';
import './Income.css';

export default function Income({ notify }) {
  const { userId, readContracts, contracts } = useContext(Web3Context);
  const [selectedPkg, setSelectedPkg] = useState(0);
  const [board, setBoard] = useState(null);
  const [cycleHistory, setCycleHistory] = useState([]);
  const [viewingCycle, setViewingCycle] = useState(-1);
  const [incomeData, setIncomeData] = useState(null);
  const [levelIncome, setLevelIncome] = useState({ total: '0' });
  const [autoLevelIncome, setAutoLevelIncome] = useState({ total: '0' });
  const [eliteClaimable, setEliteClaimable] = useState('0');
  const [globalClaimable, setGlobalClaimable] = useState('0');
  const [dividendClaimable, setDividendClaimable] = useState('0');
  const [eligibility, setEligibility] = useState({ elite: false, global: false });
  const [poolAccum, setPoolAccum] = useState(null);
  const [rewards, setRewards] = useState([]);
  const [rewardStatus, setRewardStatus] = useState({ eligible: [], given: [] });
  const [computedProgress, setComputedProgress] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [levelClaimAmt, setLevelClaimAmt] = useState('');
  const [autoClaimAmt, setAutoClaimAmt] = useState('');

  async function refreshBalances() {
    try {
      const [lvl, autoLvl, eliteC, globalC, breakdown, dash] = await Promise.all([
        readContracts.views.getClaimableLevelIncome(userId),
        readContracts.views.getClaimableAutoLevelIncome(userId),
        readContracts.views.getUserClaimableElite(userId),
        readContracts.views.getUserClaimableGlobal(userId),
        readContracts.views.getUserIncomeBreakdown(userId),
        readContracts.views.getUserDashboard(userId)
      ]);
      setLevelIncome({ total: lvl.total });
      setAutoLevelIncome({ total: autoLvl.total });
      setEliteClaimable(eliteC);
      setGlobalClaimable(globalC);
      setDividendClaimable(dash.claimableDividend);
      setIncomeData({ boardIncome: breakdown.boardIncome, directIncome: breakdown.directIncome, levelIncome: breakdown.levelIncome, autoLevelIncome: breakdown.autoLevelIncome, royaltyIncome: breakdown.royaltyIncome, dividendIncome: breakdown.dividendIncome });
    } catch { /* silent */ }
  }

  useEffect(() => {
    if (!userId || !readContracts.views) return;
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const [breakdown, lvl, autoLvl, eliteC, globalC, poolData, dash,
          eAccum, gAccum, eEligCount, gEligCount,
          rewardStatusData, rewardTierCount] = await Promise.all([
          readContracts.views.getUserIncomeBreakdown(userId),
          readContracts.views.getClaimableLevelIncome(userId),
          readContracts.views.getClaimableAutoLevelIncome(userId),
          readContracts.views.getUserClaimableElite(userId),
          readContracts.views.getUserClaimableGlobal(userId),
          readContracts.views.getUserRoyaltyEligibility(userId),
          readContracts.views.getUserDashboard(userId),
          readContracts.pools.eliteRoyaltyAccumulation(),
          readContracts.pools.globalRoyaltyAccumulation(),
          readContracts.pools.eliteRoyaltyEligibleCount(),
          readContracts.pools.globalRoyaltyEligibleCount(),
          readContracts.views.getUserRewardStatus(userId).catch(() => ({ eligible: [], given: [] })),
          readContracts.pools.rewardTierCount().catch(() => ethers.BigNumber.from(0))
        ]);
        if (cancelled) return;
        setIncomeData({ boardIncome: breakdown.boardIncome, directIncome: breakdown.directIncome, levelIncome: breakdown.levelIncome, autoLevelIncome: breakdown.autoLevelIncome, royaltyIncome: breakdown.royaltyIncome, dividendIncome: breakdown.dividendIncome });
        setLevelIncome({ total: lvl.total });
        setAutoLevelIncome({ total: autoLvl.total });
        setEliteClaimable(eliteC);
        setGlobalClaimable(globalC);
        setDividendClaimable(dash.claimableDividend);
        setEligibility({ elite: poolData.elite, global: poolData.global });
        setPoolAccum({
          eliteAccum: eAccum, globalAccum: gAccum,
          eliteEligible: eEligCount.toNumber(), globalEligible: gEligCount.toNumber()
        });
        setRewardStatus({
          eligible: Array.isArray(rewardStatusData.eligible) ? rewardStatusData.eligible : [],
          given: Array.isArray(rewardStatusData.given) ? rewardStatusData.given : []
        });

        const tc = rewardTierCount.toNumber();
        if (tc > 0) {
          const tierPs = [];
          for (let i = 0; i < tc; i++) tierPs.push(readContracts.pools.rewardTiers(i));
          const tiers = await Promise.all(tierPs);
          if (!cancelled) setRewards(tiers.map(t => ({ name: t.name, description: t.description, directsRequired: t.directsRequired.toNumber(), levelRequired: t.levelRequired.toNumber(), membersRequired: t.membersRequired.toNumber(), requireFullPackages: t.requireFullPackages, active: t.active })));

          // Compute real eligibility from on-chain data — fast (2 reads total)
          checkUserAllTiers(readContracts, userId).then(results => {
            if (!cancelled) {
              setComputedProgress(results);
              saveEligibility(userId, results); // auto-save to localStorage for admin
            }
          });
        }
      } catch (err) { if (!cancelled) notify.error('Failed to load income: ' + (err.reason || err.message)); }
      if (!cancelled) setLoading(false);
    }
    load();
    return () => { cancelled = true; };
  }, [userId, readContracts.views]);

  // Board data
  useEffect(() => {
    if (!userId || !readContracts.matrix) return;
    let cancelled = false;
    async function loadBoard() {
      try {
        // getUserBoard = corrected second level (sorted under L1 parents) for display
        // getUserBoardRaw = raw storage + closedPart for state info
        const [corrected, raw, history] = await Promise.all([
          readContracts.matrix.getUserBoard(userId, selectedPkg),
          readContracts.matrix.getUserBoardRaw(userId, selectedPkg),
          readContracts.matrix.getBoardCycleHistory(userId, selectedPkg)
        ]);
        if (cancelled) return;
        setBoard({
          referrer: corrected[0].toNumber(),
          firstLevel: [corrected[1][0].toNumber(), corrected[1][1].toNumber()],
          // Corrected: slots 0-1 under L1[0], slots 2-3 under L1[1]
          secondLevel: [corrected[2][0].toNumber(), corrected[2][1].toNumber(), corrected[2][2].toNumber(), corrected[2][3].toNumber()],
          blocked: corrected[3],
          reinvestCount: corrected[4].toNumber(),
          closedPart: raw[5].toNumber()  // closedPart only from raw
        });
        setCycleHistory(history.map(h => ({
          cycleNumber: h.cycleNumber.toNumber(),
          firstLevel: [h.firstLevelUsers[0].toNumber(), h.firstLevelUsers[1].toNumber()],
          secondLevel: [h.secondLevelUsers[0].toNumber(), h.secondLevelUsers[1].toNumber(), h.secondLevelUsers[2].toNumber(), h.secondLevelUsers[3].toNumber()],
          completedAt: h.completedAt.toNumber()
        })));
        setViewingCycle(-1);
      } catch { /* silent */ }
    }
    loadBoard();
    return () => { cancelled = true; };
  }, [userId, selectedPkg, readContracts.matrix]);

  function getDisplayBoard() {
    if (viewingCycle === -1) return board;
    const c = cycleHistory[viewingCycle];
    if (!c) return board;
    return { referrer: 0, firstLevel: c.firstLevel, secondLevel: c.secondLevel, blocked: false, reinvestCount: c.cycleNumber, closedPart: 0 };
  }

  async function handleClaimLevel() {
    if (!levelClaimAmt || parseFloat(levelClaimAmt) <= 0) { notify.warning('Enter a valid amount'); return; }
    setProcessing('level');
    try { const tx = await contracts.pools.claimLevelIncome(ethers.utils.parseUnits(levelClaimAmt, 18)); await tx.wait(); notify.success('Level income claimed!'); setLevelClaimAmt(''); await refreshBalances(); }
    catch (err) { notify.error('Claim failed: ' + (err.reason || err.message)); }
    setProcessing(null);
  }

  async function handleClaimAutoLevel() {
    if (!autoClaimAmt || parseFloat(autoClaimAmt) <= 0) { notify.warning('Enter a valid amount'); return; }
    setProcessing('autoLevel');
    try { const tx = await contracts.pools.claimAutoLevelIncome(ethers.utils.parseUnits(autoClaimAmt, 18)); await tx.wait(); notify.success('Auto level income claimed!'); setAutoClaimAmt(''); await refreshBalances(); }
    catch (err) { notify.error('Claim failed: ' + (err.reason || err.message)); }
    setProcessing(null);
  }

  async function handleClaimAllRoyalty() {
    setProcessing('allRoyalty');
    try { const tx = await contracts.pools.claimAllRoyalty(); await tx.wait(); notify.success('All royalty pools claimed!'); await refreshBalances(); }
    catch (err) { notify.error('Claim failed: ' + (err.reason || err.message)); }
    setProcessing(null);
  }

  async function handleClaimDividend() {
    setProcessing('dividend');
    try { const tx = await contracts.pools.claimDividend(); await tx.wait(); notify.success('Dividend claimed!'); await refreshBalances(); }
    catch (err) { notify.error('Claim failed: ' + (err.reason || err.message)); }
    setProcessing(null);
  }

  async function handleClaimEverything() {
    setProcessing('everything');
    try {
      const loadId = notify.loading('Claiming all income...');
      const tx = await contracts.pools.claimAll();
      await tx.wait();
      notify.removeNotification(loadId);
      notify.success('All income claimed successfully!');
      await refreshBalances();
    } catch (err) { notify.error('Claim failed: ' + (err.reason || err.message)); }
    setProcessing(null);
  }

  if (loading) return <div className="page-loading"><div className="mini-spinner" /><span>Loading...</span></div>;

  const db = getDisplayBoard();
  const REWARD_ICONS = ['📱', '✈️', '🏍️', '🚗', '🏠'];

  return (
    <div className="income-page">
      {/* ===== BOARD ===== */}
      <h2 className="page-title">X6 Board</h2>
      <div className="board-pkg-tabs">
        {PACKAGE_NAMES.map((name, i) => (
          <button key={i} className={`board-tab ${selectedPkg === i ? 'board-tab-active' : ''}`}
            style={selectedPkg === i ? { borderColor: PACKAGE_COLORS[i], color: PACKAGE_COLORS[i] } : {}}
            onClick={() => setSelectedPkg(i)}>{name}</button>
        ))}
      </div>

      {db && (() => {
        const pkColor = PACKAGE_COLORS[selectedPkg];
        const filled = db.firstLevel.filter(x => x > 0).length + db.secondLevel.filter(x => x > 0).length;
        const NodeBox = ({ id, label, size }) => (
          <div className={`bnode ${size || ''} ${id ? 'bnode-on' : 'bnode-off'}`} style={id ? { borderColor: pkColor + (size ? '60' : '90') } : {}}>
            <span className="bnode-id">{id || '---'}</span>
            <span className="bnode-lbl">{label}</span>
          </div>
        );
        return (
          <div className="board-card animate-in">
            <div className="board-nav-row">
              <button className="board-nav-btn" disabled={cycleHistory.length === 0 || viewingCycle === 0}
                onClick={() => setViewingCycle(prev => prev === -1 ? cycleHistory.length - 1 : prev - 1)}>&#9664; Prev</button>
              <div className="board-cycle-center">
                <span className="board-cycle-num">{viewingCycle === -1 ? board?.reinvestCount || 0 : cycleHistory[viewingCycle]?.cycleNumber || 0}</span>
                <span className="board-cycle-text">{viewingCycle === -1 ? 'Current Cycle' : 'History'}</span>
              </div>
              <button className="board-nav-btn" disabled={viewingCycle === -1}
                onClick={() => setViewingCycle(prev => prev >= cycleHistory.length - 1 ? -1 : prev + 1)}>Next &#9654;</button>
            </div>

            <div className="board-meta">
              <span>Upline: <strong>{db.referrer || 'N/A'}</strong></span>
              <span>{filled}/6 filled</span>
            </div>

            <div className="btree">
              {/* Root */}
              <div className="btree-row">
                <div className="bnode bnode-root" style={{ borderColor: pkColor, boxShadow: `0 0 20px ${pkColor}20` }}>
                  <span className="bnode-id">{userId}</span>
                  <span className="bnode-lbl">YOU</span>
                </div>
              </div>
              {/* Root → L1 lines */}
              <svg className="btree-svg" viewBox="0 0 300 24" preserveAspectRatio="none">
                <line x1="150" y1="0" x2="75" y2="24" stroke={pkColor + '30'} strokeWidth="1.5"/>
                <line x1="150" y1="0" x2="225" y2="24" stroke={pkColor + '30'} strokeWidth="1.5"/>
              </svg>
              {/* L1 row */}
              <div className="btree-row btree-l1">
                <NodeBox id={db.firstLevel[0]} label="L1" />
                <NodeBox id={db.firstLevel[1]} label="L1" />
              </div>
              {/* L1 → L2 lines */}
              <svg className="btree-svg btree-svg-l2" viewBox="0 0 300 20" preserveAspectRatio="none">
                <line x1="75" y1="0" x2="38" y2="20" stroke={pkColor + '20'} strokeWidth="1"/>
                <line x1="75" y1="0" x2="112" y2="20" stroke={pkColor + '20'} strokeWidth="1"/>
                <line x1="225" y1="0" x2="188" y2="20" stroke={pkColor + '20'} strokeWidth="1"/>
                <line x1="225" y1="0" x2="262" y2="20" stroke={pkColor + '20'} strokeWidth="1"/>
              </svg>
              {/* L2 row */}
              <div className="btree-row btree-l2">
                <NodeBox id={db.secondLevel[0]} label="L2" size="sm" />
                <NodeBox id={db.secondLevel[1]} label="L2" size="sm" />
                <NodeBox id={db.secondLevel[2]} label="L2" size="sm" />
                <NodeBox id={db.secondLevel[3]} label="L2" size="sm" />
              </div>
            </div>

            {/* Progress dots */}
            <div className="board-dots">
              {Array.from({ length: 6 }).map((_, i) => (
                <span key={i} className="board-dot" style={{ background: i < filled ? pkColor : 'rgba(255,255,255,0.08)' }} />
              ))}
            </div>

            {board?.blocked && <div className="board-blocked-badge">Retopup Required</div>}
          </div>
        );
      })()}

      {/* ===== INCOME BREAKDOWN ===== */}
      {/* ===== CLAIM ALL ===== */}
      <div className="claim-all-card animate-in" style={{ marginTop: 20 }}>
        <div className="claim-all-glow" />
        <span className="claim-all-label">Total Claimable</span>
        <span className="claim-all-amount">${(
          parseFloat(formatUSDT(levelIncome.total).replace(/,/g, '') || 0) +
          parseFloat(formatUSDT(autoLevelIncome.total).replace(/,/g, '') || 0) +
          parseFloat(formatUSDT(eliteClaimable).replace(/,/g, '') || 0) +
          parseFloat(formatUSDT(globalClaimable).replace(/,/g, '') || 0) +
          parseFloat(formatUSDT(dividendClaimable).replace(/,/g, '') || 0)
        ).toFixed(2)}</span>
        <button className="claim-all-btn" onClick={handleClaimEverything} disabled={!!processing}>
          <span className="claim-all-btn-pulse" />
          <span className="claim-all-btn-text">{processing === 'everything' ? 'CLAIMING...' : 'CLAIM ALL INCOME'}</span>
        </button>
      </div>

      <h2 className="page-title" style={{ marginTop: 20 }}>Income Breakdown</h2>
      {incomeData && (
        <div className="income-grid animate-in">
          {[
            { label: 'Board Income', value: incomeData.boardIncome, color: INCOME_COLORS.board },
            { label: 'Direct Income', value: incomeData.directIncome, color: INCOME_COLORS.direct },
            { label: 'Level Income', value: incomeData.levelIncome, color: INCOME_COLORS.level },
            { label: 'Auto Level', value: incomeData.autoLevelIncome, color: INCOME_COLORS.autoLevel },
            { label: ROYALTY_NAMES.elite, value: eliteClaimable, color: INCOME_COLORS.eliteRoyalty },
            { label: ROYALTY_NAMES.global, value: globalClaimable, color: INCOME_COLORS.globalRoyalty },
            { label: 'Dividend', value: incomeData.dividendIncome, color: INCOME_COLORS.dividend }
          ].map((item, i) => (
            <div key={i} className="income-item">
              <span className="income-item-dot" style={{ background: item.color }} />
              <span className="income-item-label">{item.label}</span>
              <span className="income-item-value" style={{ color: item.color }}>${formatUSDT(item.value)}</span>
            </div>
          ))}
        </div>
      )}

      {/* ===== CLAIM SECTION ===== */}
      <div className="claim-section animate-in">
        <div className="claim-card">
          <div className="claim-header"><span className="claim-type">Level Income</span><span className="claim-amount">${formatUSDT(levelIncome.total)}</span></div>
          <div className="claim-input-row">
            <input className="claim-input" type="number" placeholder="Amount" value={levelClaimAmt} onChange={e => setLevelClaimAmt(e.target.value)} />
            <button className="action-btn action-btn-primary claim-btn" onClick={handleClaimLevel} disabled={processing === 'level'}>{processing === 'level' ? '...' : 'Claim'}</button>
          </div>
        </div>
        <div className="claim-card">
          <div className="claim-header"><span className="claim-type">Auto Level Income</span><span className="claim-amount">${formatUSDT(autoLevelIncome.total)}</span></div>
          <div className="claim-input-row">
            <input className="claim-input" type="number" placeholder="Amount" value={autoClaimAmt} onChange={e => setAutoClaimAmt(e.target.value)} />
            <button className="action-btn action-btn-primary claim-btn" onClick={handleClaimAutoLevel} disabled={processing === 'autoLevel'}>{processing === 'autoLevel' ? '...' : 'Claim'}</button>
          </div>
        </div>
      </div>

      {/* ===== ROYALTY POOLS ===== */}
      <h2 className="page-title" style={{ marginTop: 20 }}>Royalty Pools</h2>
      <div className="royalty-cards animate-in">
        <div className="royalty-card">
          <div className="royalty-header">
            <span className="royalty-name">{ROYALTY_NAMES.elite}</span>
            <span className={`royalty-badge ${eligibility.elite ? 'royalty-eligible' : 'royalty-not'}`}>
              {eligibility.elite ? 'Eligible' : 'Not Eligible'}
            </span>
          </div>
          <div className="royalty-amount">${formatUSDT(eliteClaimable)}</div>
          <div className="royalty-req">Requires: First 4 packages active + 3 directs with first 4 packages</div>
        </div>
        <div className="royalty-card">
          <div className="royalty-header">
            <span className="royalty-name">{ROYALTY_NAMES.global}</span>
            <span className={`royalty-badge ${eligibility.global ? 'royalty-eligible' : 'royalty-not'}`}>
              {eligibility.global ? 'Eligible' : 'Not Eligible'}
            </span>
          </div>
          <div className="royalty-amount">${formatUSDT(globalClaimable)}</div>
          <div className="royalty-req">Requires: All 7 packages active + 3 directs with all 7 packages</div>
        </div>
        {(eligibility.elite || eligibility.global) && (
          <button className="action-btn action-btn-primary" onClick={handleClaimAllRoyalty} disabled={processing === 'allRoyalty'} style={{ width: '100%' }}>
            {processing === 'allRoyalty' ? 'Processing...' : 'Claim All Royalty Pools'}
          </button>
        )}
      </div>

      {/* ===== DIVIDEND ===== */}
      <h2 className="page-title" style={{ marginTop: 20 }}>Dividend Pool</h2>
      <div className="royalty-cards animate-in">
        <div className="royalty-card">
          <div className="royalty-header">
            <span className="royalty-name">Dividend Income</span>
            <span className="royalty-badge royalty-eligible">10% Auto</span>
          </div>
          <div className="royalty-amount" style={{ color: INCOME_COLORS.dividend }}>${formatUSDT(dividendClaimable)}</div>
          <div className="royalty-req">10% of every package activation distributed to all active package holders</div>
          {parseFloat(formatUSDT(dividendClaimable).replace(/,/g, '')) > 0 && (
            <button className="action-btn action-btn-primary" onClick={handleClaimDividend} disabled={processing === 'dividend'} style={{ width: '100%', marginTop: 8 }}>
              {processing === 'dividend' ? 'Processing...' : 'Claim Dividend'}
            </button>
          )}
        </div>
      </div>

      {/* ===== REWARDS ===== */}
      <h2 className="page-title" style={{ marginTop: 20 }}>Rewards</h2>
      <div className="rewards-list animate-in">
        {rewards.map((tier, i) => {
          const progress = computedProgress[i];
          const isComputing = !progress;
          const isEligible = progress && progress.eligible;
          const isGiven = rewardStatus.given[i];
          let statusLabel = 'Locked';
          if (isGiven) statusLabel = 'Given';
          else if (isComputing) statusLabel = 'Checking...';
          else if (isEligible) statusLabel = 'Achieved';
          return (
            <div key={i} className={`reward-card ${isEligible ? 'reward-eligible' : ''}`}>
              <div className="reward-header">
                <span className="reward-icon">{REWARD_ICONS[i % REWARD_ICONS.length]}</span>
                <div className="reward-info">
                  <span className="reward-name">{tier.name}</span>
                  <span className="reward-desc">{tier.description}</span>
                  {progress && (
                    <span className="reward-progress-text">{progress.count}/{progress.required} qualified</span>
                  )}
                </div>
                <span className={`reward-status ${isGiven ? 'reward-given' : isEligible ? 'reward-achieved' : 'reward-locked'}`}>
                  {statusLabel}
                </span>
              </div>
              <div className="reward-requirements">
                {tier.directsRequired > 0 && <span className="reward-req-tag">{tier.directsRequired} Direct Full Packages</span>}
                {tier.levelRequired > 0 && <span className="reward-req-tag">{tier.membersRequired} Members at Level {tier.levelRequired}</span>}
                {tier.requireFullPackages && <span className="reward-req-tag">All Packages Required</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
